import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, X, Filter, Calendar } from "lucide-react";
import { resolveF } from "./constants";

const OwnerTypeahead = ({ owners, loading, value, hasToken, onChange }) => {
  const [query,    setQuery]    = useState("");
  const [open,     setOpen]     = useState(false);
  const [focused,  setFocused]  = useState(false);
  const ref = React.useRef(null);

  const selected = owners.find(o => o.id === value);

  const filtered = query.length > 0
    ? owners.filter(o =>
        (o.name||"").toLowerCase().includes(query.toLowerCase()) ||
        (o.email||"").toLowerCase().includes(query.toLowerCase())
      )
    : owners;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (loading) return (
    <div style={{padding:"9px 12px",background:"#F0F0F0",borderRadius:7,fontSize:12,color:"#555555",border:"1px solid rgba(204,26,26,0.10)",marginBottom:14}}>Loading owners…</div>
  );

  if (!owners.length) return (
    <div style={{padding:"9px 12px",background:"#F0F0F0",borderRadius:7,fontSize:12,color:"#555555",border:"1px solid rgba(204,26,26,0.10)",marginBottom:14}}>
      {hasToken ? "No owners found — click Refresh owners" : "Connect HubSpot first →"}
    </div>
  );

  return (
    <div ref={ref} style={{position:"relative",marginBottom:14}}>
      <div style={{position:"relative"}}>
        <input
          value={focused ? query : (selected ? selected.name : "")}
          placeholder="Type to search owners…"
          onFocus={() => { setFocused(true); setQuery(""); setOpen(true); }}
          onBlur={() => { setTimeout(() => { setFocused(false); setOpen(false); }, 150); }}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          style={{
            width:"100%", background:"#F0F0F0",
            border:`1px solid ${value ? "rgba(22,163,74,0.12)" : "rgba(204,26,26,0.10)"}`,
            borderRadius:7, padding:"9px 32px 9px 12px",
            color: value && !focused ? "#16A34A" : "#111111",
            fontSize:13, outline:"none", boxSizing:"border-box"
          }}
        />
        {value && (
          <button onClick={() => { onChange({id:"",name:""}); setQuery(""); }}
            style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"transparent",border:"none",color:"#555555",cursor:"pointer",fontSize:14,padding:2}}>
            ×
          </button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#FFFFFF",border:"1px solid rgba(204,26,26,0.10)",borderRadius:8,zIndex:9999,maxHeight:220,overflowY:"auto",boxShadow:"0 8px 24px rgba(0,0,0,0.15)"}}>
          {filtered.map(o => (
            <div key={o.id}
              onPointerDown={e => { e.preventDefault(); onChange(o); setOpen(false); setFocused(false); setQuery(""); }}
              style={{padding:"10px 14px",cursor:"pointer",borderBottom:"1px solid rgba(0,0,0,0.04)",
                background: o.id === value ? "rgba(204,26,26,0.10)" : "transparent"}}
              onMouseEnter={e => e.currentTarget.style.background="rgba(204,26,26,0.10)"}
              onMouseLeave={e => e.currentTarget.style.background = o.id === value ? "rgba(204,26,26,0.10)" : "transparent"}>
              <div style={{fontSize:13,fontWeight:600,color: o.id===value ? "#16A34A" : "#111111"}}>{o.name}</div>
              {o.email && <div style={{fontSize:10,color:"#555555",marginTop:2}}>{o.email}</div>}
            </div>
          ))}
        </div>
      )}
      {open && query.length > 0 && filtered.length === 0 && (
        <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#FFFFFF",border:"1px solid rgba(204,26,26,0.10)",borderRadius:8,zIndex:999,padding:"12px 14px",fontSize:12,color:"#555555"}}>
          No owners match "{query}"
        </div>
      )}
    </div>
  );
};


const FILTER_DEFAULTS = {
  dateField:  "closedate",   // "closedate" | "createdate"
  dateFrom:   "",
  dateTo:     "",
  ownerIds:   [],            // [] = all
  segments:   [],            // [] = all
  period:     "annual",      // "annual" | "q1".."q4" | "jan".."dec"
};

const QUICK_RANGES = [
  { label: "YTD",       id: "ytd" },
  { label: "This Qtr",  id: "thisqtr" },
  { label: "Last Qtr",  id: "lastqtr" },
  { label: "This Month",id: "thismonth" },
  { label: "Last 30d",  id: "30d" },
  { label: "Last 90d",  id: "90d" },
  { label: "All Time",  id: "all" },
];

const getQuickRange = (id, fiscalYear) => {
  const now   = new Date();
  const fy    = fiscalYear || now.getFullYear();
  const qtr   = Math.floor(now.getMonth() / 3);
  const qStarts = [
    new Date(`${fy}-01-01`), new Date(`${fy}-04-01`),
    new Date(`${fy}-07-01`), new Date(`${fy}-10-01`)
  ];
  const qEnds = [
    new Date(`${fy}-03-31`), new Date(`${fy}-06-30`),
    new Date(`${fy}-09-30`), new Date(`${fy}-12-31`)
  ];
  const fmt = d => d.toISOString().slice(0,10);
  if (id === "ytd")       return { from: `${fy}-01-01`, to: fmt(now) };
  if (id === "thisqtr")   return { from: fmt(qStarts[qtr]),  to: fmt(qEnds[qtr]) };
  if (id === "lastqtr")   return { from: fmt(qStarts[Math.max(0,qtr-1)]), to: fmt(qEnds[Math.max(0,qtr-1)]) };
  if (id === "thismonth") return { from: fmt(new Date(now.getFullYear(),now.getMonth(),1)), to: fmt(now) };
  if (id === "30d")       return { from: fmt(new Date(Date.now()-30*864e5)), to: fmt(now) };
  if (id === "90d")       return { from: fmt(new Date(Date.now()-90*864e5)), to: fmt(now) };
  return { from: "", to: "" };
};

const applyFilters = (deals, filters, settings) => {
  const _F = resolveF(settings);
  if (!deals?.length) return deals || [];
  return deals.filter(d => {
    // Date filter
    if (filters.dateFrom || filters.dateTo) {
      const dateVal = d.properties[filters.dateField];
      if (!dateVal) return false;
      const dt = new Date(dateVal).getTime();
      if (filters.dateFrom && dt < new Date(filters.dateFrom).getTime()) return false;
      if (filters.dateTo   && dt > new Date(filters.dateTo  + "T23:59:59").getTime()) return false;
    }
    // Owner filter
    if (filters.ownerIds?.length > 0) {
      const oid = String(d.properties[_F.owner] || "");
      if (!filters.ownerIds.includes(oid)) return false;
    }
    return true;
  });
};

// ── Global Filter Bar Component ─────────────────────
// ── Owner Multi-Select Dropdown ────────────────────
const OwnerMultiSelect = ({ owners, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = React.useRef(null);

  // Close on outside click
  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = owners.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter(s=>s!==id) : [...selected, id]);
  const clearAll = () => onChange([]);
  const selectAll = () => onChange(filtered.map(o=>o.id));

  return (
    <div ref={ref} style={{position:"relative",minWidth:220}}>
      <div style={{fontSize:10,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Rep / Owner</div>
      {/* Trigger button */}
      <button onClick={()=>setOpen(o=>!o)}
        style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",
          background:"#F0F0F0",border:`1px solid ${selected.length?"rgba(22,163,74,0.12)":"rgba(255,255,255,0.1)"}`,
          borderRadius:7,padding:"7px 10px",color:selected.length?"#16A34A":"#555555",fontSize:11,cursor:"pointer",gap:8}}>
        <span>{selected.length ? `${selected.length} rep${selected.length>1?"s":""} selected` : "All reps"}</span>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          {selected.length > 0 && (
            <span onClick={e=>{e.stopPropagation();clearAll();}}
              style={{fontSize:12,color:"#DC2626",lineHeight:1,cursor:"pointer"}}>×</span>
          )}
          <ChevronDown size={11} style={{transform:open?"rotate(180deg)":"none",transition:"0.2s"}}/>
        </div>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,zIndex:200,width:240,
          background:"#FFFFFF",border:"1px solid rgba(204,26,26,0.10)",borderRadius:8,
          boxShadow:"0 8px 32px rgba(0,0,0,0.5)",overflow:"hidden"}}>
          {/* Search */}
          <div style={{padding:"8px 10px",borderBottom:"1px solid rgba(0,0,0,0.08)"}}>
            <input autoFocus value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search reps…"
              style={{width:"100%",background:"#F0F0F0",border:"1px solid rgba(204,26,26,0.10)",borderRadius:5,
                padding:"5px 8px",color:"#111111",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
          </div>
          {/* Select all / clear */}
          <div style={{display:"flex",gap:0,borderBottom:"1px solid rgba(0,0,0,0.08)"}}>
            <button onClick={selectAll}
              style={{flex:1,background:"transparent",border:"none",padding:"6px",color:"#CC1A1A",fontSize:10,fontWeight:600,cursor:"pointer"}}>
              Select all
            </button>
            <div style={{width:1,background:"rgba(0,0,0,0.08)"}}/>
            <button onClick={clearAll}
              style={{flex:1,background:"transparent",border:"none",padding:"6px",color:"#555555",fontSize:10,cursor:"pointer"}}>
              Clear
            </button>
          </div>
          {/* Options list */}
          <div style={{maxHeight:200,overflowY:"auto"}}>
            {filtered.length === 0 && (
              <div style={{padding:"12px",fontSize:11,color:"#555555",textAlign:"center"}}>No matches</div>
            )}
            {filtered.map(o => {
              const on = selected.includes(o.id);
              return (
                <div key={o.id} onClick={()=>toggle(o.id)}
                  style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px",cursor:"pointer",
                    background:on?"rgba(22,163,74,0.12)":"transparent"}}
                  onMouseEnter={e=>e.currentTarget.style.background=on?"rgba(22,163,74,0.12)":"rgba(0,0,0,0.04)"}
                  onMouseLeave={e=>e.currentTarget.style.background=on?"rgba(22,163,74,0.12)":"transparent"}>
                  <div style={{width:14,height:14,borderRadius:3,border:`1.5px solid ${on?"#16A34A":"rgba(255,255,255,0.15)"}`,
                    background:on?"#16A34A":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {on && <span style={{fontSize:9,color:"#F8F8F8",fontWeight:800}}>✓</span>}
                  </div>
                  <span style={{fontSize:11,color:on?"#111111":"#777777",fontWeight:on?600:400}}>{o.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const FilterBar = ({ filters, setFilters, owners, settings, visible, onToggle }) => {
  const [showCustomDate, setShowCustomDate] = useState(false);
  const fy = +settings?.company?.fiscalYear || new Date().getFullYear();

  const setQuick = (id) => {
    if (id === "all") { setFilters(f => ({ ...f, dateFrom: "", dateTo: "" })); setShowCustomDate(false); return; }
    const { from, to } = getQuickRange(id, fy);
    setFilters(f => ({ ...f, dateFrom: from, dateTo: to }));
    setShowCustomDate(false);
  };

  const toggleOwner = (id) => {
    setFilters(f => ({
      ...f,
      ownerIds: f.ownerIds.includes(id) ? f.ownerIds.filter(o => o !== id) : [...f.ownerIds, id]
    }));
  };

  const clearAll = () => setFilters(FILTER_DEFAULTS);

  const hasActive = filters.dateFrom || filters.dateTo || filters.ownerIds.length > 0;

  return (
    <div style={{background:"#FFFFFF",borderBottom:"1px solid rgba(0,0,0,0.08)",flexShrink:0}}>
      {/* Filter toggle bar */}
      <div style={{padding:"6px 22px",display:"flex",alignItems:"center",gap:12}}>
        <button onClick={onToggle}
          style={{display:"flex",alignItems:"center",gap:6,background:hasActive?"rgba(0,0,0,0.08)":"transparent",
            border:`1px solid ${hasActive?"rgba(204,26,26,0.10)":"rgba(0,0,0,0.08)"}`,
            borderRadius:6,padding:"4px 10px",color:hasActive?"#CC1A1A":"#555555",fontSize:11,fontWeight:600,cursor:"pointer"}}>
          <Filter size={11}/> Filters {hasActive && `(active)`}
          <ChevronDown size={10} style={{transform:visible?"rotate(180deg)":"none",transition:"0.2s"}}/>
        </button>

        {/* Active filter chips */}
        {filters.dateFrom && (
          <div style={{display:"flex",alignItems:"center",gap:4,background:"rgba(204,26,26,0.10)",border:"1px solid rgba(204,26,26,0.10)",borderRadius:5,padding:"3px 8px",fontSize:10,color:"#CC1A1A"}}>
            📅 {filters.dateField==="closedate"?"Close":"Created"}: {filters.dateFrom} → {filters.dateTo||"now"}
            <button onClick={()=>setFilters(f=>({...f,dateFrom:"",dateTo:""}))} style={{background:"none",border:"none",color:"#CC1A1A",cursor:"pointer",fontSize:12,padding:0,marginLeft:2}}>×</button>
          </div>
        )}
        {filters.ownerIds.map(oid => {
          const o = owners.find(o=>o.id===oid);
          return o ? (
            <div key={oid} style={{display:"flex",alignItems:"center",gap:4,background:"rgba(22,163,74,0.12)",border:"1px solid rgba(22,163,74,0.12)",borderRadius:5,padding:"3px 8px",fontSize:10,color:"#16A34A"}}>
              👤 {o.name}
              <button onClick={()=>toggleOwner(oid)} style={{background:"none",border:"none",color:"#16A34A",cursor:"pointer",fontSize:12,padding:0,marginLeft:2}}>×</button>
            </div>
          ) : null;
        })}
        {hasActive && (
          <button onClick={clearAll} style={{background:"none",border:"none",color:"#DC2626",fontSize:11,cursor:"pointer",padding:0}}>
            Clear all
          </button>
        )}
      </div>

      {/* Expanded filter panel */}
      {visible && (
        <div style={{padding:"12px 22px 14px",background:"#E8E8E8",borderTop:"1px solid rgba(204,26,26,0.10)",display:"grid",gridTemplateColumns:"auto auto 1fr",gap:24,alignItems:"start"}}>

          {/* Date section */}
          <div>
            <div style={{fontSize:10,fontWeight:700,color:"#555555",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Date Filter</div>
            {/* Date field toggle */}
            <div style={{display:"flex",gap:4,marginBottom:8}}>
              {[{id:"closedate",l:"Close Date"},{id:"createdate",l:"Created Date"}].map(df=>(
                <button key={df.id} onClick={()=>setFilters(f=>({...f,dateField:df.id}))}
                  style={{padding:"3px 10px",borderRadius:5,border:`1px solid ${filters.dateField===df.id?"#CC1A1A":"rgba(0,0,0,0.08)"}`,
                    background:filters.dateField===df.id?"rgba(0,0,0,0.08)":"transparent",
                    color:filters.dateField===df.id?"#CC1A1A":"#555555",fontSize:10,fontWeight:600,cursor:"pointer"}}>
                  {df.l}
                </button>
              ))}
            </div>
            {/* Quick ranges */}
            <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:8}}>
              {QUICK_RANGES.map(r=>(
                <button key={r.id} onClick={()=>r.id==="custom"?setShowCustomDate(true):setQuick(r.id)}
                  style={{padding:"3px 8px",borderRadius:5,border:"1px solid rgba(0,0,0,0.08)",
                    background:"transparent",color:"#555555",fontSize:10,cursor:"pointer"}}
                  onMouseEnter={e=>e.target.style.borderColor="#CC1A1A"}
                  onMouseLeave={e=>e.target.style.borderColor="rgba(0,0,0,0.08)"}>
                  {r.label}
                </button>
              ))}
              <button onClick={()=>setShowCustomDate(!showCustomDate)}
                style={{padding:"3px 8px",borderRadius:5,border:`1px solid ${showCustomDate?"#CC1A1A":"rgba(0,0,0,0.08)"}`,
                  background:showCustomDate?"rgba(204,26,26,0.10)":"transparent",color:showCustomDate?"#CC1A1A":"#555555",fontSize:10,cursor:"pointer"}}>
                Custom
              </button>
            </div>
            {/* Custom date inputs */}
            {showCustomDate && (
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input type="date" value={filters.dateFrom||""} onChange={e=>setFilters(f=>({...f,dateFrom:e.target.value}))}
                  style={{background:"#F0F0F0",border:"1px solid rgba(204,26,26,0.10)",borderRadius:5,padding:"4px 8px",color:"#111111",fontSize:11,outline:"none"}}/>
                <span style={{color:"#555555",fontSize:11}}>→</span>
                <input type="date" value={filters.dateTo||""} onChange={e=>setFilters(f=>({...f,dateTo:e.target.value}))}
                  style={{background:"#F0F0F0",border:"1px solid rgba(204,26,26,0.10)",borderRadius:5,padding:"4px 8px",color:"#111111",fontSize:11,outline:"none"}}/>
              </div>
            )}
          </div>

          {/* Rep/Owner dropdown multi-select */}
          {owners.length > 0 && (
            <OwnerMultiSelect owners={owners} selected={filters.ownerIds} onChange={ids=>setFilters(f=>({...f,ownerIds:ids}))}/>
          )}

          {/* Stats */}
          <div style={{justifySelf:"end",alignSelf:"center"}}>
            {(filters.dateFrom || filters.ownerIds.length > 0) && (
              <div style={{fontSize:10,color:"#555555",textAlign:"right"}}>
                Filters applied — all metrics update automatically
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── POR Period Selector ─────────────────────────────
const getPeriodDateRange = (period, fiscalYear) => {
  const fy = fiscalYear || new Date().getFullYear();
  const qMap = { q1:[0,2], q2:[3,5], q3:[6,8], q4:[9,11] };
  const mMap = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11 };
  if (period === "annual") return { from:`${fy}-01-01`, to:`${fy}-12-31` };
  if (qMap[period]) {
    const [sm,em] = qMap[period];
    const lastDay = new Date(fy, em+1, 0).getDate();
    return { from:`${fy}-${String(sm+1).padStart(2,"0")}-01`, to:`${fy}-${String(em+1).padStart(2,"0")}-${lastDay}` };
  }
  if (mMap[period] !== undefined) {
    const m = mMap[period];
    const lastDay = new Date(fy, m+1, 0).getDate();
    return { from:`${fy}-${String(m+1).padStart(2,"0")}-01`, to:`${fy}-${String(m+1).padStart(2,"0")}-${lastDay}` };
  }
  return { from:`${fy}-01-01`, to:`${fy}-12-31` };
};

const getPeriodTarget = (period, annualTarget, monthlyTargets) => {
  const t = +annualTarget || 0;
  const mt = monthlyTargets || {};
  const qMonths = { q1:["jan","feb","mar"], q2:["apr","may","jun"], q3:["jul","aug","sep"], q4:["oct","nov","dec"] };
  if (period === "annual") return t;
  if (qMonths[period]) {
    const qSum = qMonths[period].reduce((a,m) => a + (+mt[m]||0), 0);
    return qSum > 0 ? qSum : t / 4;
  }
  // Monthly
  const monthVal = +mt[period] || 0;
  return monthVal > 0 ? monthVal : t / 12;
};


export {
  OwnerTypeahead,
  FILTER_DEFAULTS, QUICK_RANGES, getQuickRange, applyFilters,
  OwnerMultiSelect, FilterBar,
  getPeriodDateRange, getPeriodTarget,
};
